const SKIP_USERDATA_KEYS = new Set([
  'mixer',
  '_root',
  'parent',
  'children',
  'geometry',
  'material',
  'scene',
  'camera',
  'renderer',
]);

function isNonSerializableObject(value) {
  if (!value || typeof value !== 'object') return false;
  if (typeof value.isObject3D === 'boolean') return true;
  if (typeof value.isMaterial === 'boolean') return true;
  if (typeof value.isTexture === 'boolean') return true;
  if (typeof value.isBufferGeometry === 'boolean') return true;
  if (value.constructor?.name === 'AnimationMixer') return true;
  if (value.constructor?.name === 'AnimationAction') return true;
  if (value.constructor?.name === 'WebGLRenderer') return true;
  return false;
}

/**
 * Deep-clone plain data for undo/redo snapshots.
 * Skips Three.js objects, functions, DOM nodes, and circular references.
 */
export function safeHistoryClone(value, seen = new WeakSet()) {
  if (value === null || value === undefined) return value;

  const valueType = typeof value;
  if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
    return value;
  }
  if (valueType === 'function' || valueType === 'symbol' || valueType === 'bigint') {
    return undefined;
  }

  if (isNonSerializableObject(value)) {
    return undefined;
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (typeof Element !== 'undefined' && value instanceof Element) {
    return undefined;
  }

  if (seen.has(value)) {
    return undefined;
  }

  if (Array.isArray(value)) {
    seen.add(value);
    return value
      .map((item) => safeHistoryClone(item, seen))
      .filter((item) => item !== undefined);
  }

  seen.add(value);
  const output = {};
  Object.entries(value).forEach(([key, nested]) => {
    if (SKIP_USERDATA_KEYS.has(key)) return;
    if (typeof nested === 'function') return;
    if (isNonSerializableObject(nested)) return;

    const cloned = safeHistoryClone(nested, seen);
    if (cloned !== undefined) {
      output[key] = cloned;
    }
  });
  return output;
}

export function safeHistoryCloneOrEmpty(value) {
  const cloned = safeHistoryClone(value);
  if (cloned === undefined) return {};
  return cloned;
}
